import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Scissors, ArrowLeft } from "lucide-react";
import { useAuth } from "@/lib/auth";

/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */
function startGoogleAuth() {
  const redirect = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
}

export default function AuthPage({ mode }) {
  const [sp] = useSearchParams();
  const initialRole = sp.get("role") === "groomer" ? "groomer" : "client";
  const nav = useNavigate();
  const { login, register } = useAuth();
  const [role, setRole] = useState(initialRole);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    email: "", password: "", name: "", phone: "", address: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handle = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let u;
      if (mode === "login") {
        u = await login(form.email, form.password);
      } else {
        u = await register({ ...form });  // role forced to "client" server-side
      }
      toast.success(`Welcome, ${u.name.split(" ")[0]}!`);
      nav(u.role === "groomer" ? "/groomer" : "/client");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  const handleDemo = async (email, password) => {
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Signed in as ${u.name}`);
      nav(u.role === "groomer" ? "/groomer" : "/client");
    } catch (e) {
      toast.error("Demo login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex flex-col">
      <header className="max-w-7xl w-full mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-[#1F1A17]" data-testid="back-home">
          <div className="w-9 h-9 rounded-xl bg-[#C85A17] grid place-content-center text-white">
            <Scissors className="w-4 h-4"/>
          </div>
          <span className="font-display text-xl font-bold">PawTrek</span>
        </Link>
        <Link to="/" className="text-sm text-[#6B625B] flex items-center gap-1 hover:text-[#1F1A17]">
          <ArrowLeft className="w-4 h-4"/> Home
        </Link>
      </header>

      <main className="flex-1 grid place-items-center px-6 pb-12">
        <div className="w-full max-w-md">
          <div className="bg-white border border-[#E5DCCE] rounded-3xl p-8 shadow-sm">
            <h1 className="font-display text-3xl font-bold mb-1">
              {mode === "login" ? "Welcome back" : "Create your account"}
            </h1>
            <p className="text-sm text-[#6B625B] mb-6">
              {mode === "login" ? "Sign in to continue" : "Two-sided access for groomers and clients"}
            </p>

            <Button onClick={startGoogleAuth} variant="outline"
              className="w-full h-11 rounded-full border-[#E5DCCE] hover:bg-[#F3EDE4] gap-2 mb-4"
              data-testid="btn-google-auth">
              <GoogleG/>
              Continue with Google
            </Button>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#E5DCCE]"/></div>
              <div className="relative flex justify-center text-[10px] uppercase tracking-wider">
                <span className="bg-white px-2 text-[#6B625B]">or use email</span>
              </div>
            </div>

            {mode === "register" && (
              <Tabs value={role} onValueChange={setRole} className="mb-4">
                <TabsList className="grid grid-cols-1 w-full bg-[#F3EDE4]">
                  <TabsTrigger value="client" data-testid="tab-client">Sign up as a client</TabsTrigger>
                </TabsList>
                <TabsContent value="client"/>
              </Tabs>
            )}

            <form onSubmit={handle} className="space-y-3">
              {mode === "register" && (
                <>
                  <div>
                    <Label>Full name</Label>
                    <Input required value={form.name} onChange={set("name")} data-testid="auth-name"/>
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input value={form.phone} onChange={set("phone")} data-testid="auth-phone"/>
                  </div>
                  <div>
                    <Label>Address</Label>
                    <Input value={form.address} onChange={set("address")} data-testid="auth-address"
                      placeholder="Where should the van meet you?"/>
                  </div>
                </>
              )}
              <div>
                <Label>Email</Label>
                <Input type="email" required value={form.email} onChange={set("email")}
                  data-testid="auth-email"/>
              </div>
              <div>
                <Label>Password</Label>
                <Input type="password" required minLength={6} value={form.password}
                  onChange={set("password")} data-testid="auth-password"/>
              </div>

              <Button type="submit" disabled={busy}
                className="w-full rounded-full bg-[#C85A17] hover:bg-[#A9480F] btn-tactile mt-2"
                data-testid="auth-submit">
                {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-[#E5DCCE] text-center text-sm">
              {mode === "login" ? (
                <>New here? <Link to="/register" className="text-[#C85A17] font-semibold" data-testid="link-register">Create an account</Link></>
              ) : (
                <>Already have an account? <Link to="/login" className="text-[#C85A17] font-semibold" data-testid="link-login">Sign in</Link></>
              )}
            </div>
          </div>

          <div className="mt-4 rounded-2xl bg-[#F5E6CF] border border-amber-900/10 p-4 text-xs text-[#6B4A18]">
            <div className="font-bold uppercase tracking-wider mb-2">Try the demo</div>
            <div className="flex flex-col gap-2">
              <button onClick={() => handleDemo("charliemcbride36@gmail.com", "Groomer123!")}
                className="text-left underline underline-offset-2 hover:text-[#A9480F]"
                data-testid="demo-groomer">
                Groomer → charliemcbride36@gmail.com
              </button>
              <button onClick={() => handleDemo("demo.client@pawtrek.app", "Client123!")}
                className="text-left underline underline-offset-2 hover:text-[#A9480F]"
                data-testid="demo-client">
                Client → demo.client@pawtrek.app
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}
